<!--
    BD Contract Decision Table — Wave 3
    Pro-Vertrag-Tabelle mit Entscheidungs-Dropdown und Submit pro Zeile.
    @ticket TM-1000001786
-->
@if (initialized) {
    <div class="flex flex-column gap-3">

        <!-- Header -->
        <div class="flex align-items-center gap-2">
            <i class="fa-light fa-file-contract text-primary"></i>
            <span class="font-semibold text-lg">Vertragsentscheidungen</span>
        </div>

        <!-- Error -->
        @if (errorMessage) {
            <p-message severity="error" [text]="errorMessage" styleClass="w-full"></p-message>
        }

        <!-- Empty state -->
        @if (rows.length === 0) {
            <div class="surface-card border-round p-4 text-center">
                <i class="fa-light fa-folder-open text-3xl text-400 mb-2"></i>
                <p class="m-0 text-600">Keine Verträge zur Bearbeitung verfügbar.</p>
            </div>
        }

        <!-- Decision table -->
        @if (rows.length > 0) {
            <div class="surface-card border-round overflow-hidden">
                <p-table [value]="rows"
                         styleClass="p-datatable-sm p-datatable-striped">

                    <ng-template pTemplate="header">
                        <tr>
                            <th class="w-6rem">Vertrag-ID</th>
                            <th>Vertrags-Name</th>
                            <th class="w-8rem text-right">Mahn-Saldo</th>
                            <th class="w-16rem">Vermittler-Hinweis</th>
                            <th class="w-14rem">Aktion</th>
                            <th class="w-8rem text-center">Bestätigen</th>
                        </tr>
                    </ng-template>

                    <ng-template pTemplate="body" let-row>
                        <tr [class.bd-row-submitted]="row.submitted">
                            <td class="font-mono text-sm">{{ row.contractId }}</td>
                            <td>
                                {{ row.contractName }}
                                <!-- C19: LV-Posten kennzeichnen -->
                                @if (row.isLvContract) {
                                    <span class="ml-2 text-xs border-round px-2 py-1 bg-purple-100 text-purple-700 font-semibold"
                                          pTooltip="Lebensversicherung — Bearbeitung über den Sachbearbeiter-Arbeitsvorrat">LV</span>
                                }
                            </td>
                            <td class="text-right font-medium">
                                {{ row.dunningBalance | currency:'EUR':'symbol':'1.2-2':'de-DE' }}
                            </td>
                            <td>
                                <!-- C19: Klartext statt anonymem Icon; kein Default, keine harte Sperre -->
                                @if (row.agentHint) {
                                    <div class="flex flex-column gap-1 text-xs">
                                        <span class="text-orange-700 font-semibold">
                                            <i class="fa-light fa-circle-info mr-1"></i>Inkasso-Übergabe nicht vorgesehen
                                        </span>
                                        @if (row.agentNumber) {
                                            <span class="text-color-secondary">Vermittlernummer: {{ row.agentNumber }}</span>
                                        }
                                    </div>
                                } @else {
                                    <span class="text-400">—</span>
                                }
                            </td>
                            <td>
                                <p-select [options]="decisionOptions"
                                          optionLabel="label"
                                          optionValue="value"
                                          [(ngModel)]="row.selectedAction"
                                          (onChange)="onDecisionChange(row)"
                                          placeholder="Auswählen…"
                                          [disabled]="row.submitted || row.submitting"
                                          appendTo="body"
                                          styleClass="w-full">
                                </p-select>
                            </td>
                            <td class="text-center">
                                @if (row.submitted) {
                                    <i class="fa-solid fa-circle-check text-green-500"
                                       pTooltip="Erfolgreich gespeichert"></i>
                                } @else {
                                    <p-button label="Auswahl bestätigen"
                                              icon="fa-light fa-check"
                                              size="small"
                                              [loading]="row.submitting"
                                              [disabled]="!canSubmit(row)"
                                              (onClick)="submitRow(row)">
                                    </p-button>
                                }
                            </td>
                        </tr>

                        <!-- Rate-Agreement sub-dialog inline (Ratenzahlung selected) -->
                        @if (row.rateAgreement.showDialog) {
                            <tr>
                                <td colspan="6" class="surface-50">
                                    <div class="flex flex-column gap-3 p-3">
                                        <div class="text-sm font-semibold">
                                            <i class="fa-light fa-calendar-days mr-1"></i>
                                            Ratenvereinbarung — Parameter
                                        </div>
                                        <div class="grid">
                                            <div class="col-12 md:col-6">
                                                <label class="text-xs text-600 block mb-1">
                                                    Gesamtbetrag (EUR)
                                                </label>
                                                <p-inputNumber [(ngModel)]="row.rateAgreement.totalAmount"
                                                               mode="decimal"
                                                               [minFractionDigits]="2"
                                                               [maxFractionDigits]="2"
                                                               placeholder="Mahn-Saldo (Vorgabe)"
                                                               styleClass="w-full">
                                                </p-inputNumber>
                                                <small class="text-500">Leer = Mahn-Saldo</small>
                                            </div>
                                            <div class="col-12 md:col-6">
                                                <label class="text-xs text-600 block mb-1">
                                                    Anzahl Raten <span class="text-red-500">*</span>
                                                </label>
                                                <p-inputNumber [(ngModel)]="row.rateAgreement.monthsCount"
                                                               [min]="1"
                                                               [max]="60"
                                                               styleClass="w-full">
                                                </p-inputNumber>
                                            </div>
                                        </div>

                                        <!-- G10: Cent-genaue Vorschau aus dem geteilten Util (wie im Dialog) -->
                                        @if (ratePlanInstallments(row).length > 0) {
                                            <div class="surface-card border-round overflow-hidden">
                                                <p-table [value]="ratePlanInstallments(row)"
                                                         styleClass="p-datatable-sm p-datatable-striped">
                                                    <ng-template pTemplate="header">
                                                        <tr>
                                                            <th class="w-4rem">Rate Nr.</th>
                                                            <th>Betrag</th>
                                                            <th>Fälligkeit</th>
                                                        </tr>
                                                    </ng-template>
                                                    <ng-template pTemplate="body" let-item let-last="last">
                                                        <tr [ngClass]="{'surface-50': last && ratePlan(row).lastRateDiffers}">
                                                            <td class="font-semibold">{{ item.index }}</td>
                                                            <td>
                                                                {{ item.amount | currency:'EUR':'symbol':'1.2-2':'de-DE' }}
                                                                @if (last && ratePlan(row).lastRateDiffers) {
                                                                    <span class="text-xs text-color-secondary ml-2">(letzte Rate)</span>
                                                                }
                                                            </td>
                                                            <td>{{ item.dueDate | date:'dd.MM.yyyy' }}</td>
                                                        </tr>
                                                    </ng-template>
                                                    <ng-template pTemplate="footer">
                                                        <tr>
                                                            <td class="font-semibold">Σ</td>
                                                            <td class="font-semibold">
                                                                {{ ratePlan(row).installmentsSum | currency:'EUR':'symbol':'1.2-2':'de-DE' }}
                                                            </td>
                                                            <td class="text-sm text-color-secondary">Summe der Raten = Gesamtbetrag</td>
                                                        </tr>
                                                    </ng-template>
                                                </p-table>
                                            </div>

                                            <!-- A3: Hinweis auf abweichende letzte Rate (Cent-Rest) -->
                                            @if (ratePlan(row).lastRateDiffers) {
                                                <div class="text-xs text-color-secondary flex align-items-center gap-2">
                                                    <i class="fa-light fa-circle-info"></i>
                                                    <span>Die letzte Rate trägt den Cent-Rest und kann vom Auto-Betrag abweichen.</span>
                                                </div>
                                            }
                                        }

                                        <!-- G10: Plausibilität (cent-genau) aus dem geteilten Util -->
                                        @if (isRatePlanImplausible(row)) {
                                            <p-message
                                                    severity="warn"
                                                    text="Summe der Raten weicht vom Gesamtbetrag ab. Bitte Anzahl der Raten prüfen."
                                                    styleClass="w-full">
                                            </p-message>
                                        }

                                        <!-- Plan-Wirkungs-Hinweis -->
                                        <div class="surface-50 border-round p-2 text-xs text-color-secondary flex align-items-start gap-2">
                                            <i class="fa-light fa-circle-info mt-1"></i>
                                            <span>Mit Plan: Mahnung pausiert, Heute-Saldo 0&nbsp;€, Vorausschau = Rückstand.</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }

                        <!-- Write-off sub-fields (Pflicht-Begründung, C05) -->
                        @if (row.selectedAction === 'WRITE_OFF') {
                            <tr>
                                <td colspan="6" class="surface-50">
                                    <div class="grid p-3">
                                        <div class="col-12 md:col-4">
                                            <label class="text-xs text-600 block mb-1">
                                                Abschreibungsbetrag
                                            </label>
                                            <p-inputNumber [(ngModel)]="row.writeOff.amount"
                                                           mode="decimal"
                                                           [minFractionDigits]="2"
                                                           [maxFractionDigits]="2"
                                                           styleClass="w-full">
                                            </p-inputNumber>
                                        </div>
                                        <div class="col-12 md:col-8">
                                            <label class="text-xs text-600 block mb-1">
                                                Begründung <span class="text-red-500">*</span>
                                            </label>
                                            <input type="text"
                                                   pInputText
                                                   [(ngModel)]="row.writeOff.reason"
                                                   placeholder="Begründung der Abschreibung (Pflichtfeld)"
                                                   class="w-full"/>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }

                        <!-- Pflicht-Begründung für Inkasso-Übergabe / Sonderbehandlung (C05) -->
                        @if (row.selectedAction === 'HANDOVER' || row.selectedAction === 'SPECIAL_HANDLING') {
                            <tr>
                                <td colspan="6" class="surface-50">
                                    <div class="p-3">
                                        <label class="text-xs text-600 block mb-1">
                                            Begründung <span class="text-red-500">*</span>
                                        </label>
                                        <textarea pInputTextarea
                                                  [(ngModel)]="row.reason"
                                                  rows="2"
                                                  placeholder="Bitte die Entscheidung begründen (Pflichtfeld)"
                                                  class="w-full"></textarea>
                                    </div>
                                </td>
                            </tr>
                        }

                        <!-- G5: Wiederinkraftsetzung — reversibel, Pflicht-Begründung + optionaler Stichtag -->
                        @if (row.selectedAction === 'REACTIVATE') {
                            <tr>
                                <td colspan="6" class="surface-50">
                                    <div class="flex flex-column gap-3 p-3">
                                        <!-- Wirkungs-Hinweis: Das passiert, wenn du klickst -->
                                        <div class="surface-100 border-round p-3 text-sm flex align-items-start gap-2">
                                            <i class="fa-light fa-rotate-left text-primary mt-1"></i>
                                            <span>
                                                Mit dem Klick wird der Vertrag wieder in Kraft gesetzt. Ohne Stichtag
                                                erfolgt die Wiederinkraftsetzung <span class="font-semibold">unbefristet</span>;
                                                mit Stichtag gilt sie bis zum angegebenen Datum (befristet).
                                                Diese Aktion ist umkehrbar.
                                            </span>
                                        </div>
                                        <div class="grid">
                                            <div class="col-12 md:col-5">
                                                <label class="text-xs text-600 block mb-1">
                                                    Stichtag (optional)
                                                </label>
                                                <p-datePicker [(ngModel)]="row.reactivateEffectiveDate"
                                                              dateFormat="dd.mm.yy"
                                                              [showIcon]="true"
                                                              [showButtonBar]="true"
                                                              appendTo="body"
                                                              placeholder="leer = unbefristet"
                                                              styleClass="w-full">
                                                </p-datePicker>
                                                <small class="text-500">Leer = unbefristet</small>
                                            </div>
                                        </div>
                                        <div class="flex flex-column gap-1">
                                            <label class="text-xs text-600 block mb-1">
                                                Begründung <span class="text-red-500">*</span>
                                            </label>
                                            <textarea pInputTextarea
                                                      [(ngModel)]="row.reason"
                                                      rows="2"
                                                      placeholder="Bitte die Wiederinkraftsetzung begründen (Pflichtfeld)"
                                                      class="w-full"></textarea>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }

                        <!-- G6: Storno — irreversibel, Pflicht-Stichtag + Pflicht-Begründung -->
                        @if (row.selectedAction === 'STORNO') {
                            <tr>
                                <td colspan="6" class="surface-50">
                                    <div class="flex flex-column gap-3 p-3">
                                        <!-- Wirkungs-Hinweis: Das passiert, wenn du klickst -->
                                        <div class="surface-100 border-round p-3 text-sm flex align-items-start gap-2">
                                            <i class="fa-light fa-ban text-red-600 mt-1"></i>
                                            <span>
                                                Mit dem Klick wird der Vertrag zum angegebenen Stichtag
                                                <span class="font-semibold">storniert</span>. Diese Aktion ist
                                                <span class="font-semibold text-red-600">nicht umkehrbar</span> und
                                                erfordert eine Bestätigung.
                                            </span>
                                        </div>
                                        <div class="grid">
                                            <div class="col-12 md:col-5">
                                                <label class="text-xs text-600 block mb-1">
                                                    Stichtag <span class="text-red-500">*</span>
                                                </label>
                                                <p-datePicker [(ngModel)]="row.stornoEffectiveDate"
                                                              dateFormat="dd.mm.yy"
                                                              [showIcon]="true"
                                                              appendTo="body"
                                                              placeholder="Stichtag wählen"
                                                              styleClass="w-full">
                                                </p-datePicker>
                                            </div>
                                        </div>
                                        <div class="flex flex-column gap-1">
                                            <label class="text-xs text-600 block mb-1">
                                                Begründung <span class="text-red-500">*</span>
                                            </label>
                                            <textarea pInputTextarea
                                                      [(ngModel)]="row.reason"
                                                      rows="2"
                                                      placeholder="Bitte den Storno begründen (Pflichtfeld)"
                                                      class="w-full"></textarea>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        }
                    </ng-template>

                </p-table>
            </div>
        }

    </div>

    <!-- Bestätigungs-Rückfrage vor irreversibler Aktion (C05) -->
    <p-dialog header="Aktion bestätigen"
              [visible]="pendingConfirmation !== null"
              [modal]="true"
              [closable]="false"
              [draggable]="false"
              [style]="{ width: '32rem' }">
        @if (pendingConfirmation) {
            <div class="flex flex-column gap-3">
                <p class="m-0 text-700">
                    Bitte prüfen Sie die folgende, nicht umkehrbare Aktion:
                </p>
                <div class="surface-100 border-round p-3 flex flex-column gap-2">
                    <div class="flex justify-content-between gap-3">
                        <span class="text-600">Vertrag</span>
                        <span class="font-medium text-right">{{ pendingConfirmation.contractName }}</span>
                    </div>
                    <div class="flex justify-content-between gap-3">
                        <span class="text-600">Betrag</span>
                        <span class="font-medium text-right">
                            {{ pendingConfirmation.amount | currency:'EUR':'symbol':'1.2-2':'de-DE' }}
                        </span>
                    </div>
                    <div class="flex justify-content-between gap-3">
                        <span class="text-600">Aktion</span>
                        <span class="font-medium text-right">{{ pendingConfirmation.actionLabel }}</span>
                    </div>
                    <div class="flex justify-content-between gap-3">
                        <span class="text-600">Begründung</span>
                        <span class="font-medium text-right">{{ pendingConfirmation.reason }}</span>
                    </div>
                </div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Abbrechen"
                          icon="fa-light fa-xmark"
                          severity="secondary"
                          [outlined]="true"
                          (onClick)="cancelPending()">
                </p-button>
                <p-button label="Bestätigen"
                          icon="fa-light fa-check"
                          severity="danger"
                          (onClick)="confirmPending()">
                </p-button>
            </ng-template>
        }
    </p-dialog>
}
